import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useMentions } from '../../api/useMentions';
import { type MentionSuggestion } from '../../api/mentions';
import { plainTextFromDom } from './mentionUtils';
import {
  buildChipNode,
  caretToEndOf,
  DRAFT_REGEX,
  getCaretOffset,
  isSelfOrAncestorChip,
  MENTION_DRAFT_CLASS,
  moveCaretAfter,
  QUERY_REGEX,
  setCaretOffset,
} from './mentionDom';

const SUGGEST_DEBOUNCE_MS = 350;
// Stable identity while the query is in flight — the highlight resets off
// `suggestions` changing, and a fresh `[]` each render would reset forever.
const NO_SUGGESTIONS: MentionSuggestion[] = [];

interface Options {
  /** The contenteditable root the caret lives in. */
  rootRef: RefObject<HTMLDivElement>;
  /** Runs after a chip is inserted, with the editor's fresh plain text. */
  onInserted: (_plainText: string) => void;
}

// Owns the `@mention` trigger lifecycle: draft span, popup query + position,
// suggestion fetching, and chip insertion.
export const useMentionSuggestions = ({ rootRef, onInserted }: Options) => {
  // The live draft span wrapping the in-progress `@query`, if any.
  const draftRef = useRef<HTMLSpanElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // `query` drives popup visibility and the "No matches for …" label.
  // `debouncedQuery` is the queryKey passed to TanStack — lags by
  // SUGGEST_DEBOUNCE_MS so we don't fire on every keystroke.
  const [query, setQuery] = useState<string | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{
    bottom: number;
    left: number;
  } | null>(null);

  const { data, isFetching: loading } = useMentions(debouncedQuery);
  const suggestions = data ?? NO_SUGGESTIONS;

  // Debounce query → debouncedQuery so we don't fire on every keystroke.
  useEffect(() => {
    if (query === null) return;
    debounceRef.current = setTimeout(
      () => setDebouncedQuery(query),
      SUGGEST_DEBOUNCE_MS
    );
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // Highlight resets to 0 whenever fresh suggestions arrive.
  const [highlight, setHighlight] = useState({ source: suggestions, index: 0 });
  if (highlight.source !== suggestions) {
    setHighlight({ source: suggestions, index: 0 });
  }
  const setHighlightedIndex = useCallback(
    (value: SetStateAction<number>) =>
      setHighlight((h) => ({
        ...h,
        index: typeof value === 'function' ? value(h.index) : value,
      })),
    []
  );

  // Replace the draft span with its plain text so a dismissed mention stops
  // rendering in the mention color. No-op when there's no active draft.
  // replaceChild + normalize drops the selection, so the browser reset the
  // caret to the top of the editor — typing after a dismissed @mention landed
  // in front of it (CORE-4736). Capture the caret offset first and restore it
  // (the unwrapped text is identical, so the offset is unchanged).
  const unwrapDraft = useCallback(() => {
    const span = draftRef.current;
    draftRef.current = null;
    const parent = span?.parentNode;
    const root = rootRef.current;
    if (!span || !parent) return;
    const caret = root ? getCaretOffset(root) : null;
    parent.replaceChild(document.createTextNode(span.textContent ?? ''), span);
    parent.normalize();
    if (root && caret !== null) setCaretOffset(root, caret);
  }, [rootRef]);

  const closePopup = useCallback(() => {
    unwrapDraft();
    setQuery(null);
    // Cleared with the popup so stale results don't flash on re-open.
    setDebouncedQuery(null);
    setPopupPosition(null);
  }, [unwrapDraft]);

  // Position the popup above the input. It opens upward, with its bottom
  // edge clearing the whole input container (not just the `@` glyph) so the
  // list never overlaps the field; horizontally it aligns to the `@`.
  const positionAbove = useCallback((el: HTMLElement) => {
    const container = el.closest(
      '[data-mention-input-container]'
    ) as HTMLElement | null;
    const anchorTop = (container ?? el).getBoundingClientRect().top;
    setPopupPosition({
      bottom: window.innerHeight - anchorTop + 8,
      left: el.getBoundingClientRect().left,
    });
  }, []);

  // Inspect the caret and decide whether a mention trigger is active. While
  // active the `@query` lives in a draft span (so it renders in the mention
  // color); this keeps that span in sync, growing/closing it as the user
  // types, and drives the popup query + position.
  const detectTrigger = useCallback(() => {
    const sel = window.getSelection();
    const root = rootRef.current;
    if (!sel || !root || sel.rangeCount === 0) {
      if (query !== null) closePopup();
      return;
    }
    const range = sel.getRangeAt(0);
    const caretNode = range.startContainer;
    if (!range.collapsed || !root.contains(caretNode)) {
      if (query !== null) closePopup();
      return;
    }

    const draft = draftRef.current;

    // Caret sits inside the active draft span — keep it open while the text
    // still looks like `@word`, otherwise dismiss.
    if (draft && draft.contains(caretNode)) {
      const m = (draft.textContent ?? '').match(DRAFT_REGEX);
      if (m) {
        positionAbove(draft);
        setQuery(m[1]);
        return;
      }
      closePopup();
      return;
    }

    // A character can land in the text node right after the draft span when
    // typing at its boundary — pull that leading run back into the span so
    // the mention keeps growing colored.
    if (
      draft &&
      caretNode.nodeType === Node.TEXT_NODE &&
      caretNode.previousSibling === draft
    ) {
      const tn = caretNode as Text;
      const txt = tn.textContent ?? '';
      const wsIdx = txt.search(/\s/);
      const take = wsIdx === -1 ? txt : txt.slice(0, wsIdx);
      if (take) {
        draft.textContent = (draft.textContent ?? '') + take;
        tn.textContent = txt.slice(take.length);
        caretToEndOf(draft);
      }
      const m = (draft.textContent ?? '').match(DRAFT_REGEX);
      if (m) {
        positionAbove(draft);
        setQuery(m[1]);
        return;
      }
      closePopup();
      return;
    }

    // Fresh trigger in a plain text node — wrap `@query` into a draft span.
    if (
      caretNode.nodeType !== Node.TEXT_NODE ||
      isSelfOrAncestorChip(caretNode)
    ) {
      if (query !== null) closePopup();
      return;
    }
    const textNode = caretNode as Text;
    const offset = range.startOffset;
    const before = (textNode.textContent ?? '').slice(0, offset);
    const match = before.match(QUERY_REGEX);
    if (!match) {
      if (query !== null) closePopup();
      return;
    }
    const q = match[1];
    const atOffset = offset - q.length - 1;
    const wrapRange = document.createRange();
    wrapRange.setStart(textNode, atOffset);
    wrapRange.setEnd(textNode, offset);
    const span = document.createElement('span');
    span.className = MENTION_DRAFT_CLASS;
    try {
      wrapRange.surroundContents(span);
    } catch {
      if (query !== null) closePopup();
      return;
    }
    draftRef.current = span;
    caretToEndOf(span);
    positionAbove(span);
    setQuery(q);
  }, [closePopup, positionAbove, query, rootRef]);

  // Insert the chosen suggestion as an atomic chip in place of the draft
  // span. Caret lands after a trailing space.
  const insertMention = useCallback(
    (suggestion: MentionSuggestion) => {
      const root = rootRef.current;
      const span = draftRef.current;
      if (!root || !span || !span.parentNode) return;

      const chip = buildChipNode({
        id: suggestion.id,
        entity: suggestion.entity,
        name: suggestion.name,
      });
      span.parentNode.replaceChild(chip, span);
      draftRef.current = null;

      // Trailing space lets the user keep typing without immediately
      // re-triggering the popup.
      const spacer = document.createTextNode(' ');
      chip.after(spacer);

      moveCaretAfter(spacer);
      closePopup();
      onInserted(plainTextFromDom(root));
    },
    [closePopup, onInserted, rootRef]
  );

  return {
    query,
    suggestions,
    loading,
    highlightedIndex: highlight.index,
    setHighlightedIndex,
    popupPosition,
    closePopup,
    detectTrigger,
    insertMention,
  };
};
